import { getDocument, getDocumentsByQuery } from './firebase';
import { db } from './firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { RouteDailyStatus } from '../types/inventory';

// Deprecated local VehicleDailyStatus interface kept for minimal compatibility in consumers that type-narrow,
// but operational checks are now strictly route-first via RouteDailyStatus.
export interface VehicleDailyStatus {
  id: string;
  beatId: string;
  vehicleId: string;
  vehicleNumber: string;
  salesmanId: string;
  date: string;
  status: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled';
  issuedAt?: any;
  confirmedAt?: any;
  settledAt?: any;
  createdBy: string;
  createdAt: any;
  updatedAt: any;
}

export interface BeatConstraints {
  canCreateBeat: boolean;
  canEditVehicle: boolean;
  canEditDate: boolean;
  canEditRoute: boolean;
  canEditSalesman: boolean;
  constraints: string[];
  warnings: string[];
}

/**
 * Check if a vehicle is available for assignment on a specific date
 */
export const checkVehicleAvailability = async (
  _vehicleId: string,
  _date: string,
  _excludeBeatId?: string
): Promise<{ available: boolean; reason?: string; currentStatus?: string }> => {
  // Route-first: vehicle availability no longer blocks operations; keep function for backward compatibility.
  return { available: true };
};

/** Route-first: Check if a route is available (not locked by status) for a specific date */
export const checkRouteAvailability = async (
  routeId: string,
  date: string,
  excludeBeatId?: string
): Promise<{ available: boolean; reason?: string; currentStatus?: string }> => {
  try {
    const routeStatusId = `${routeId}_${date}`;
    const routeStatus = await getDocument('route_daily_status', routeStatusId) as RouteDailyStatus | null;

    if (!routeStatus) return { available: true };

    if (excludeBeatId && (routeStatus as any).beatId === excludeBeatId) {
      return { available: true };
    }

    if (routeStatus.status && ['issuing', 'on_field', 'settling', 'settled'].includes(routeStatus.status)) {
      return {
        available: false,
        reason: `Route is ${routeStatus.status} for ${date}`,
        currentStatus: routeStatus.status
      };
    }

    return { available: true };
  } catch (error) {
    console.error('Error checking route availability:', error);
    return { available: false, reason: 'Error checking route status' };
  }
};

/**
 * Check if a salesman is available for assignment on a specific date
 */
export const checkSalesmanAvailability = async (
  salesmanId: string,
  date: string,
  excludeBeatId?: string
): Promise<{ available: boolean; reason?: string; currentAssignments?: any[] }> => {
  try {
    // Route-first: base availability on beats assignments for the date
    const beatsForSalesman = await getDocumentsByQuery('beats', 'salesmanId', '==', salesmanId) as any[];
    const dateFiltered = beatsForSalesman.filter(b => (b.forDate || b.date) === date);
    if (dateFiltered.length === 0) return { available: true };

    const relevant = excludeBeatId ? dateFiltered.filter(b => b.id !== excludeBeatId) : dateFiltered;
    if (relevant.length === 0) return { available: true };

    // If multiple beats exist for the salesman on the same date, warn/block create (business rule may vary)
    return {
      available: true,
      currentAssignments: relevant
    };
  } catch (error) {
    console.error('Error checking salesman availability:', error);
    return { available: false, reason: 'Error checking salesman status' };
  }
};

/**
 * Check if a date is valid for beat operations (not in the past)
 */
export const checkDateValidity = (date: string): { valid: boolean; reason?: string } => {
  const today = new Date().toISOString().split('T')[0];
  const selectedDate = new Date(date);
  const todayDate = new Date(today);
  
  if (selectedDate < todayDate) {
    return {
      valid: false,
      reason: 'Cannot create or edit beats for past dates'
    };
  }
  
  return { valid: true };
};

/**
 * Get comprehensive beat constraints for create/edit operations
 */
export const getBeatConstraints = async (
  vehicleId?: string,
  date?: string,
  salesmanId?: string,
  routeId?: string,
  beatId?: string, // For edit operations
  isEdit: boolean = false
): Promise<BeatConstraints> => {
  const constraints: string[] = [];
  const warnings: string[] = [];
  
  let canCreateBeat = true;
  let canEditVehicle = true;
  let canEditDate = true;
  let canEditRoute = true;
  let canEditSalesman = true;
  
  // Check date validity
  if (date) {
    const dateCheck = checkDateValidity(date);
    if (!dateCheck.valid) {
      constraints.push(dateCheck.reason!);
      canCreateBeat = false;
      canEditVehicle = false;
      canEditDate = false;
      canEditRoute = false;
      canEditSalesman = false;
    }
  }
  
  // Route-first: Check route availability/status instead of vehicle
  if (routeId && date) {
    const routeCheck = await checkRouteAvailability(routeId, date, beatId);
    if (!routeCheck.available) {
      constraints.push(routeCheck.reason!);
      canCreateBeat = false;
      canEditVehicle = false;
      canEditDate = false;
      canEditSalesman = false;
    }
  }
  
  // Check salesman availability
  if (salesmanId && date) {
    const salesmanCheck = await checkSalesmanAvailability(salesmanId, date, beatId);
    if (!salesmanCheck.available) {
      constraints.push(salesmanCheck.reason!);
      if (!isEdit) {
        canCreateBeat = false;
      }
      canEditSalesman = false;
    } else if (salesmanCheck.currentAssignments && salesmanCheck.currentAssignments.length > 0) {
      warnings.push(`Salesman has ${salesmanCheck.currentAssignments.length} other assignment(s) in planning stage for this date`);
    }
  }
  
  return {
    canCreateBeat,
    canEditVehicle,
    canEditDate,
    canEditRoute,
    canEditSalesman,
    constraints,
    warnings
  };
};

/**
 * Generate sequential beat name with format YYYY-MM-DD-XXX
 */
export const generateBeatSequentialName = async (date: string): Promise<string> => {
  try {
    const counterRef = doc(db, 'counters', 'beatSerial');
    
    const newBeatName = await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);
      
      let currentCount = 1;
      if (counterDoc.exists()) {
        currentCount = (counterDoc.data().count || 0) + 1;
      }
      
      // Update the counter
      transaction.set(counterRef, { 
        count: currentCount,
        lastUpdated: serverTimestamp()
      });
      
      // Format beat name: YYYY-MM-DD-XXX (3-digit padded sequence)
      const sequenceNumber = currentCount.toString().padStart(3, '0');
      const beatName = `${date}-${sequenceNumber}`;
      return beatName;
    });
    
    return newBeatName;
  } catch (error) {
    console.error('Error generating sequential beat name:', error);
    throw error;
  }
};

/**
 * Generate auto beat name (legacy support - now uses sequential naming)
 */
export const generateBeatName = async (date: string): Promise<string> => {
  return await generateBeatSequentialName(date);
};

/**
 * Get operational status summary for a date
 */
export const getDateOperationalSummary = async (date: string) => {
  try {
    const routeStatuses = await getDocumentsByQuery('route_daily_status', 'date', '==', date) as RouteDailyStatus[];

    const summary = {
      totalRoutes: routeStatuses.length,
      planning: routeStatuses.filter(v => !v.status || v.status === 'planning').length,
      issuing: routeStatuses.filter(v => v.status === 'issuing').length,
      on_field: routeStatuses.filter(v => v.status === 'on_field').length,
      settling: routeStatuses.filter(v => v.status === 'settling').length,
      settled: routeStatuses.filter(v => v.status === 'settled').length
    };

    return { summary, details: routeStatuses };
  } catch (error) {
    console.error('Error getting operational summary:', error);
    return { summary: null, details: [] };
  }
};
